import { apiRequest } from "./api-request";
import { ModelConfigError } from "./model-config";

// Which tools the agent is offered where (GET /ai/agent-tools → agent-service /agent/tools). The
// section map is compile-time data the server owns, so a control that only belongs beside a tool
// asks here instead of keeping a second copy of it (@adr SPARK-0051-the-company-search-mode-is-a-setting@1).
//
// The payload carries the eval runner's start questions too, which nothing here reads. If that ever
// proves heavy the fix is a projection on the server, not a copy of the map in spark.

// The sections a tool is offered in, in the AGENT-SERVICE's spelling (`MATCH`, `SAVE` + `PROFILE`);
// empty `subsections` = the whole section.
export interface ToolSectionOffer {
  section: string;
  subsections: string[];
}

export interface OfferedTool {
  name: string;
  sections: ToolSectionOffer[];
  // In the registry for the graph, never offered to the model — so never a reason to show anything.
  engineOnly?: boolean;
}

export async function loadOfferedTools(): Promise<OfferedTool[]> {
  const { response } = await apiRequest("ai/agent-tools", { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to load agent tools (${response.status})`);
  return (await response.json()) as OfferedTool[];
}

/**
 * Is `name` offered on `section` — spark's lowercase `save/profile`, against the catalogue's
 * `SAVE` + `PROFILE`? A section the catalogue lists with no subsections offers the tool throughout it.
 */
export function isToolOffered(tools: OfferedTool[], name: string, section: string): boolean {
  const [main, sub] = section.toUpperCase().split("/");
  const entry = tools.find((t) => t.name === name);
  if (!entry || entry.engineOnly === true) return false;
  return entry.sections.some(
    (offer) =>
      offer.section === main &&
      (offer.subsections.length === 0 || (sub !== undefined && offer.subsections.includes(sub))),
  );
}
